// ----------------------------------------------------------------------------
// dawseur — loop-based DAW SPA (ACID Pro 1.0 inspired)
// ----------------------------------------------------------------------------
// Layout: transport (top) + track-list (left) + timeline (center) + bucket (right)
//

const { prompter } = require('../../libs/qs');
// Features:
//   - Pick, Paint, Play: drag samples from bucket → draw on timeline
//   - Real-time time-stretch via playbackRate (Phase 1)
//   - ACIDized WAV metadata parsing (acid ` RIFF chunk)
//   - faderH controls for track volume/pan
//   - Export panel integration (reuse shared bucket-export)
// ----------------------------------------------------------------------------

const initLegacyBucket = require('./legacy-bucket-integration.js');
const { decodeAIFF, isAIFF } = require('./aiff-decoder.js');
const SplitModule = require('split-grid');
const Split = SplitModule.default || SplitModule;

const { createPlaybackEngine, stretchBufferOffline } = require('./dawseur/playback-engine.js');
const { createTimeline } = require('./dawseur/timeline.js');
const { createTrackList } = require('./dawseur/track-list.js');
const { createFxRack } = require('./dawseur/fx-rack.js');
const { createMixer } = require('./dawseur/mixer.js');
const { createAutoEditor } = require('./dawseur/auto-editor.js');
const { parseAcidChunk, ACID_TYPE_NAMES } = require('./dawseur/acid-wav-parser.js');
const automation = require('./dawseur/automation.js');
const { renderToWav, setOfflineSampleLoader } = require('./dawseur/offline-render.js');
const { createStepSequencer } = require('./dawseur/step-sequencer.js');
const { createSlicer } = require('./dawseur/slicer.js');
const { createPianoRoll } = require('./dawseur/piano-roll.js');
const { saveSong, loadSong, downloadSong } = require('./dawseur/song-file.js');

// ---- Theme setup (same pattern as xtrc/patch) ----
const storedTheme = localStorage.getItem('currentTheme');
const currentTheme = storedTheme ? JSON.parse(storedTheme) : window.Theme;
window.themeColors = Object.values(currentTheme.data);
window.currentTheme = currentTheme;
window.CONTROLZ = {};

// ---- State ----
let bucket = null;
let exportModal = null;
let engine = null;
let timeline = null;
let trackList = null;
let fxRack = null;
let mixer = null;
let autoEditor = null;
let stepSequencer = null;
let showPanel = null;
let slicer = null;
let pianoRoll = null;
let domInitialized = false;
let suppressTimelineSync = false; // set during song load to prevent syncTimelineTracks

// ---- DOM refs ----
const dom = {
   playBtn: document.getElementById('lw-play'),
   stopBtn: document.getElementById('lw-stop'),
   loopBtn: document.getElementById('lw-loop'),
   bpmInput: document.getElementById('lw-bpm'),
   tempoUpBtn: document.getElementById('lw-tempoup'),
   tempoDownBtn: document.getElementById('lw-tempodown'),
   tapTempoBtn: document.getElementById('lw-taptempo'),
   positionDisplay: document.getElementById('lw-position'),
   filenameInput: document.getElementById('lw-filename'),
   exportBtn: document.getElementById('lw-export'),
   saveBtn: document.getElementById('lw-save'),
   loadBtn: document.getElementById('lw-load'),
   loadFileInput: document.getElementById('lw-load-file'),
   canvas: document.getElementById('lw-timeline-canvas'),
   timelineArea: document.querySelector('.lw-timeline-area'),
   // Toolbar
   toolSnap: document.getElementById('lw-tool-snap'),
   toolUndo: document.getElementById('lw-tool-undo'),
   toolRedo: document.getElementById('lw-tool-redo'),
   // Context menu
   contextMenu: document.getElementById('lw-context-menu'),
   // Scrollbar
   scrollbar: document.getElementById('lw-scrollbar'),
   scrollbarHandle: document.getElementById('lw-scrollbar-handle'),
};

// ---- Audio context (lazy init on first user gesture) ----
function ensureEngine() {
   if (engine) return engine;
   engine = createPlaybackEngine();
   if (mixer && mixer.setEngine) mixer.setEngine(engine);
   if (timeline && engine.ctx) timeline.state.audioCtx = engine.ctx;
   // Add all existing tracks from track list to the engine
   if (trackList) {
      for (const track of trackList.getTracks()) {
         engine.addTrack(track.id);
      }
   }
   // Wire MIDI sample loader (loads bucket or temp cache samples for step seq / slicer / piano roll)
   if (engine.setMIDISampleLoader) {
      engine.setMIDISampleLoader(async (sampleId) => {
         return await loadItemBuffer(sampleId);
      });
      console.log('[dawseur] MIDI sample loader wired in ensureEngine');
   }
   return engine;
}

// ---- Split grid (resizable panels) ----
function initSplit() {
   const wrapper = document.getElementById('wrapper');
   const panelArea = document.querySelector('.panel-area');
   if (!wrapper) return;
   let splitInstance = null;
   let rowSplitInstance = null;

   // Bucket gutter (panel-area | gutter | bucket): NOT handled by split-grid.
   // #wrapper's grid-template-columns uses var(--shared-bucket-width, 320px),
   // and split-grid's parser cannot handle var() — it would crash.
   // Instead we drive the bucket width manually via the CSS variable.
   const bucketGutter = wrapper.querySelector('.gutter-col-1');
   if (bucketGutter) {
      const STORAGE_KEY = 'dawseur-bucket-width';
      const MIN_W = 200;
      const MAX_MARGIN = 240;
      const saved = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
      if (Number.isFinite(saved) && saved >= MIN_W) {
         document.documentElement.style.setProperty('--shared-bucket-width', saved + 'px');
      }
      let dragging = false;
      const onMove = (e) => {
         if (!dragging) return;
         const vw = window.innerWidth;
         let w = vw - e.clientX;
         const maxW = Math.max(MIN_W, vw - MAX_MARGIN);
         w = Math.max(MIN_W, Math.min(maxW, w));
         document.documentElement.style.setProperty('--shared-bucket-width', w + 'px');
      };
      const onUp = () => {
         if (!dragging) return;
         dragging = false;
         bucketGutter.classList.remove('active');
         document.body.style.cursor = '';
         document.body.style.userSelect = '';
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
         const cur = getComputedStyle(document.documentElement).getPropertyValue('--shared-bucket-width').trim();
         const px = parseInt(cur, 10);
         if (Number.isFinite(px)) localStorage.setItem(STORAGE_KEY, String(px));
      };
      bucketGutter.addEventListener('mousedown', (e) => {
         e.preventDefault();
         dragging = true;
         bucketGutter.classList.add('active');
         document.body.style.cursor = 'col-resize';
         document.body.style.userSelect = 'none';
         window.addEventListener('mousemove', onMove);
         window.addEventListener('mouseup', onUp);
      });
   }

   // Sample search gutter: manual drag handler (split-grid can't parse var()).
   const ssGutter = wrapper.querySelector('.gutter-col-ss');
   if (ssGutter) {
      const STORAGE_KEY = 'dawseur-sample-width';
      const MIN_W = 160;
      const MAX_W = 500;
      const saved = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
      if (Number.isFinite(saved) && saved >= MIN_W) {
         wrapper.style.setProperty('--dawseur-ss-width', saved + 'px');
      }
      let dragging = false;
      const onMove = (e) => {
         if (!dragging) return;
         let w = e.clientX;
         w = Math.max(MIN_W, Math.min(MAX_W, w));
         wrapper.style.setProperty('--dawseur-ss-width', w + 'px');
      };
      const onUp = () => {
         if (!dragging) return;
         dragging = false;
         ssGutter.classList.remove('active');
         document.body.style.cursor = '';
         document.body.style.userSelect = '';
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
         const w = wrapper.style.getPropertyValue('--dawseur-ss-width');
         const px = parseInt(w, 10);
         if (Number.isFinite(px)) localStorage.setItem(STORAGE_KEY, String(px));
      };
      ssGutter.addEventListener('mousedown', (e) => {
         e.preventDefault();
         dragging = true;
         ssGutter.classList.add('active');
         document.body.style.cursor = 'col-resize';
         document.body.style.userSelect = 'none';
         window.addEventListener('mousemove', onMove);
         window.addEventListener('mouseup', onUp);
      });
   }

   // Row split: transport | main | gutter | fx-rack | fx-rack/mixer (on .panel-area)
   if (panelArea) {
      const rowGutter = panelArea.querySelector('.gutter-row-1');
      if (rowGutter) {
         try {
            rowSplitInstance = Split({
               rowGutters: [{
                  track: 2,
                  element: rowGutter,
               }],
               rowMinSizes: { 1: 100, 3: 80 },
               rowSnapOffset: 0,
            });
         } catch (e) {
            console.warn('[dawseur] split-grid row init failed:', e);
         }
      }
   }
}

// ---- Bucket integration ----
function initBucket() {
   const result = initLegacyBucket({
      appName: 'dawseur',
      bucketContentSelector: '.xtrc-bucket-panel .bucketcontent',
      bucketPanelSelector: '.xtrc-bucket-panel',
      onItemDrop: (item) => {
         // Handle bucket item dropped onto timeline
         handleBucketItemDrop(item);
      },
   });
   bucket = result.bucket;
   exportModal = result.exportModal;
   window.exportModal = exportModal;
}

// ---- Temporary sample cache (for samples dragged from search panel) ----
// Stores fetched samples without adding them to the bucket.
const tempSampleCache = new Map();

// ---- Resolve a drag dataTransfer into an item-like object ----
// Handles: bucket item IDs, samplebank JSON (fetches from server).
// For samplebank drags, the sample is fetched and cached in tempSampleCache
// (NOT added to the bucket). Returns {id, name, wav} or null.
async function resolveDropItem(dataTransfer) {
   // 1. Bucket item ID (bucket sets application/x-bucket-item-id)
   const bucketItemId = dataTransfer.getData('application/x-bucket-item-id');
   if (bucketItemId && bucket && bucket.items) {
      const item = bucket.items.find(it => it.id === bucketItemId);
      if (item) return item;
   }
   // 2. Samplebank JSON from sample search panel
   const raw = dataTransfer.getData('text/plain');
   if (raw) {
      try {
         const parsed = JSON.parse(raw);
         if (parsed && parsed.type === 'samplebank' && (parsed.uuid || parsed.hash || parsed.filename)) {
            const sampleId = parsed.uuid || parsed.hash || parsed.filename;
            const name = parsed.filename || parsed.name || 'sample';
            const tempId = 'temp::samplebank::' + sampleId;
            // Return cached if already fetched
            if (tempSampleCache.has(tempId)) {
               return tempSampleCache.get(tempId);
            }
            const url = '/media/get/' + encodeURIComponent(sampleId) + '?format=samplebank';
            const resp = await fetch(url);
            if (!resp.ok) return null;
            const arrayBuffer = await resp.arrayBuffer();
            const item = { id: tempId, name: name, wav: arrayBuffer };
            tempSampleCache.set(tempId, item);
            return item;
         }
      } catch (_) {}
   }
   // 3. Fallback: try text/plain as bucket item ID (some drags only set text/plain)
   if (raw && bucket && bucket.items) {
      const item = bucket.items.find(it => it.id === raw);
      if (item) return item;
   }
   return null;
}

// ---- Get arrayBuffer from an item (bucket or temp) ----
function getItemArrayBuffer(item) {
   if (!item) return null;
   if (item.wav instanceof ArrayBuffer) return item.wav;
   if (item.wav instanceof Uint8Array) return item.wav.buffer;
   if (item.wavLike instanceof ArrayBuffer) return item.wavLike;
   if (item.wavLike instanceof Uint8Array) return item.wavLike.buffer;
   if (item.data instanceof ArrayBuffer) return item.data;
   if (item.data instanceof Uint8Array) return item.data.buffer;
   return null;
}

// ---- Load audio buffer from any item (bucket or temp cache) ----
// ---- Decode audio buffer with AIFF fallback ----
// Tries decodeAudioData first, falls back to AIFF parser for .aif/.aiff files
async function decodeAudioWithFallback(ctx, arrayBuffer) {
   // Try native decodeAudioData first
   try {
      const buf = await ctx.decodeAudioData(arrayBuffer.slice(0));
      if (buf) return buf;
   } catch (e) {
      // Fall through to AIFF fallback
   }
   // Fallback: AIFF parser (Chrome/Firefox don't support AIFF in decodeAudioData)
   if (isAIFF(arrayBuffer)) {
      try {
         const buf = await decodeAIFF(ctx, arrayBuffer.slice(0));
         if (buf) return buf;
      } catch (e) {
         console.warn('[dawseur] AIFF decode failed:', e);
      }
   }
   return null;
}

async function loadItemBuffer(itemId) {
   // Check temp cache first
   if (tempSampleCache.has(itemId)) {
      const item = tempSampleCache.get(itemId);
      const ab = getItemArrayBuffer(item);
      if (ab) {
         const eng = ensureEngine();
         return await decodeAudioWithFallback(eng.ctx, ab);
      }
   }
   // Then check bucket
   if (bucket && bucket.items) {
      const item = bucket.items.find(it => it.id === itemId);
      if (item) {
         const ab = getItemArrayBuffer(item);
         if (ab) {
            const eng = ensureEngine();
            return await decodeAudioWithFallback(eng.ctx, ab);
         }
      }
   }
   return null;
}

// ---- Handle drag from bucket to timeline ----
// dropX/dropY are canvas-relative coordinates (optional — falls back to playhead)
async function handleBucketItemDrop(item, dropX, dropY) {
   if (!item || !trackList) return;

   // Determine drop position: use mouse coords if provided, else playhead
   let startBeat = timeline.state.playheadBeat || 0;
   let track;

   if (dropX !== undefined && dropY !== undefined && timeline) {
      const drop = timeline.getDropPosition(dropX, dropY);
      startBeat = drop.beat;
      // Find or create the target track
      if (drop.trackId !== null) {
         track = trackList.getTrack(drop.trackId);
      }
   }

   // Fallback: use first track or create one
   if (!track) {
      track = trackList.getTracks()[0];
      if (!track) {
         track = trackList.addTrack(item.name || 'Loop Track');
      }
   }

   // Decode the audio — works for both bucket items and temp cache items
   const eng = ensureEngine();
   const arrayBuffer = getItemArrayBuffer(item);
   if (!arrayBuffer) {
      console.warn('[dawseur] cannot get arrayBuffer from item', item);
      return;
   }

   // Try native decodeAudioData, fall back to AIFF parser
   const buffer = await decodeAudioWithFallback(eng.ctx, arrayBuffer);
   if (!buffer) return;

   // Calculate event length from buffer duration at project BPM
   // (ACID metadata is parsed but not used for stretching — always stretch to fit event length)
   const acidMeta = parseAcidChunk(arrayBuffer);
   const bpm = parseFloat(dom.bpmInput.value) || 120;
   const lengthBeats = buffer.duration * bpm / 60;

   const event = {
      startBeat,
      lengthBeats: Math.max(0.25, lengthBeats),
      volume: 0.5,
      stretched: false,       // default: play at natural speed (trim/loop), not time-stretched
      buffer,
      acidMeta,
   };

   timeline.addEventToTrack(track.id, event);

   // Add event to engine track (with per-event buffer)
   const engTrack = eng.getTrack(track.id);
   if (engTrack) {
      engTrack.events.push({
         startBeat: event.startBeat,
         lengthBeats: event.lengthBeats,
         volume: event.volume,
         stretched: event.stretched,
         buffer,
         acidMeta,
      });
   }
}

// ---- Transport handlers ----
function initTransport() {
   if (!dom.playBtn) return;

   dom.playBtn.addEventListener('click', () => {
      const eng = ensureEngine();
      if (eng.state.isPlaying) {
         eng.pause();
         dom.playBtn.classList.remove('active');
         dom.playBtn.innerHTML = '\uE800';
      } else {
         eng.play();
         dom.playBtn.classList.add('active');
         dom.playBtn.innerHTML = '\uE801';
      }
   });

   dom.stopBtn.addEventListener('click', () => {
      const eng = ensureEngine();
      eng.stop();
      dom.playBtn.classList.remove('active');
      dom.playBtn.innerHTML = '\uE800';
      if (timeline) timeline.setPlayhead(0);
      // Stop all panel previews
      if (stepSequencer && stepSequencer.stopPreview) stepSequencer.stopPreview();
      if (slicer && slicer.stopPreview) slicer.stopPreview();
      if (pianoRoll && pianoRoll.stopPreview) pianoRoll.stopPreview();
   });

   dom.loopBtn.addEventListener('click', () => {
      const isActive = dom.loopBtn.classList.toggle('active');
      if (timeline) {
         const loop = timeline.getLoop();
         timeline.state.loopEnabled = isActive;
         const eng = ensureEngine();
         eng.setLoop(isActive, loop.start, loop.end);
         timeline.render();
      }
   });

   function applyBpm(bpm) {
      const clamped = Math.max(40, Math.min(300, bpm));
      dom.bpmInput.value = clamped;
      const eng = ensureEngine();
      eng.setBpm(clamped);
      if (timeline) timeline.setBpm(clamped);
   }

   dom.bpmInput.addEventListener('change', () => {
      applyBpm(parseFloat(dom.bpmInput.value) || 120);
   });

   // Tempo nudge buttons
   if (dom.tempoUpBtn) {
      dom.tempoUpBtn.addEventListener('click', () => {
         applyBpm(parseFloat(dom.bpmInput.value || 120) + 1);
      });
   }
   if (dom.tempoDownBtn) {
      dom.tempoDownBtn.addEventListener('click', () => {
         applyBpm(parseFloat(dom.bpmInput.value || 120) - 1);
      });
   }

   // Tap tempo
   if (dom.tapTempoBtn) {
      let tapCount = 0, tapFirst = 0, tapPrev = 0;
      dom.tapTempoBtn.addEventListener('click', () => {
         const now = Date.now();
         if (now - tapPrev > 3000) tapCount = 0;
         if (tapCount === 0) {
            tapFirst = now;
            tapCount = 1;
         } else {
            const avg = 60000 * tapCount / (now - tapFirst);
            applyBpm(Math.round(avg));
            tapCount++;
         }
         tapPrev = now;
      });
   }

   // Position display + VU meter update
   setInterval(() => {
      if (engine && engine.state.isPlaying) {
         dom.positionDisplay.textContent = engine.getPositionString();
         const beat = engine.getPositionBeats();
         if (timeline) {
            timeline.setPlayhead(beat);
         }
         // Tick automations — sync timeline autoTracks so multi-clip playback works
         if (automation) {
            if (timeline && timeline.state.autoTracks) {
               automation.setExternalAutoTracks(timeline.state.autoTracks);
            }
            automation.tickAutomations(beat, engine);
         }
      }
      // Always update VU meters (shows 0 when not playing)
      if (trackList && engine) {
         const levels = {};
         for (const t of trackList.getTracks()) {
            levels[t.id] = engine.state.isPlaying ? engine.getTrackLevel(t.id) : 0;
         }
         trackList.updateVUMeters(levels);
      }
   }, 25);
}

// ---- Toolbar + keyboard shortcuts ----
function initToolbar() {
   if (!dom.toolSnap) return;

   // Snap toggle
   dom.toolSnap.addEventListener('click', () => {
      if (!timeline) return;
      timeline.state.snapEnabled = !timeline.state.snapEnabled;
      dom.toolSnap.classList.toggle('active', timeline.state.snapEnabled);
   });
   if (dom.toolUndo) {
      dom.toolUndo.addEventListener('click', () => {
         if (timeline) timeline.undo();
         updateUndoRedoButtons();
      });
   }
   if (dom.toolRedo) {
      dom.toolRedo.addEventListener('click', () => {
         if (timeline) timeline.redo();
         updateUndoRedoButtons();
      });
   }

   function updateUndoRedoButtons() {
      if (!timeline) return;
      if (dom.toolUndo) dom.toolUndo.disabled = timeline.state.undoStack.length === 0;
      if (dom.toolRedo) dom.toolRedo.disabled = timeline.state.redoStack.length === 0;
   }

   // Update undo/redo buttons after keyboard shortcuts too
   const _origUndo = timeline ? timeline.undo : null;
   const _origRedo = timeline ? timeline.redo : null;

   // Keyboard shortcuts
   document.addEventListener('keydown', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

      // Space = play/stop
      if (e.code === 'Space') {
         e.preventDefault();
         dom.playBtn.click();
         return;
      }

      // Edit shortcuts
      if (e.ctrlKey || e.metaKey) {
         if (e.key === 'a' || e.key === 'A') {
            e.preventDefault();
            if (timeline) timeline.selectAll();
         } else if (e.key === 'c' || e.key === 'C') {
            e.preventDefault();
            if (timeline) {
               // If an auto clip is selected, copy it; otherwise copy events
               if (timeline.state.selectedAutoClip) {
                  timeline.copyAutoClip(timeline.state.selectedAutoClip);
               } else {
                  timeline.copySelected();
                  timeline.state.autoClipboard = null; // clear auto clipboard
               }
            }
         } else if (e.key === 'x' || e.key === 'X') {
            e.preventDefault();
            if (timeline) {
               if (timeline.state.selectedAutoClip) {
                  timeline.copyAutoClip(timeline.state.selectedAutoClip);
                  timeline.deleteAutoClip(timeline.state.selectedAutoClip);
                  timeline.state.selectedAutoClip = null;
               } else {
                  timeline.cutSelected();
                  timeline.state.autoClipboard = null; // clear auto clipboard
               }
            }
         } else if (e.key === 'v' || e.key === 'V') {
            e.preventDefault();
            if (timeline) {
               // Priority: if an auto clip is selected, paste auto to same track.
               // If autoClipboard has data, paste auto to clipboard's source track.
               // Otherwise, paste events.
               if (timeline.state.selectedAutoClip) {
                  if (!timeline.state.autoClipboard) {
                     timeline.copyAutoClip(timeline.state.selectedAutoClip);
                  }
                  timeline.pasteAutoClip(timeline.state.playheadBeat, timeline.state.selectedAutoClip.parentId);
               } else if (timeline.state.autoClipboard) {
                  timeline.pasteAutoClip(timeline.state.playheadBeat, timeline.state.autoClipboard.parentId);
               } else {
                  timeline.pasteAt(timeline.state.playheadBeat);
               }
            }
         } else if (e.key === 'd' || e.key === 'D') {
            e.preventDefault();
            if (timeline) {
               if (timeline.state.selectedAutoClip) {
                  timeline.duplicateAutoClip(timeline.state.selectedAutoClip);
               } else {
                  timeline.duplicateSelected();
               }
            }
         } else if (e.key === 'z' || e.key === 'Z') {
            e.preventDefault();
            if (timeline) timeline.undo();
            if (dom.toolUndo) dom.toolUndo.disabled = timeline.state.undoStack.length === 0;
            if (dom.toolRedo) dom.toolRedo.disabled = timeline.state.redoStack.length === 0;
         } else if (e.key === 'y' || e.key === 'Y') {
            e.preventDefault();
            if (timeline) timeline.redo();
            if (dom.toolUndo) dom.toolUndo.disabled = timeline.state.undoStack.length === 0;
            if (dom.toolRedo) dom.toolRedo.disabled = timeline.state.redoStack.length === 0;
         } else if (e.key === 's' || e.key === 'S') {
            e.preventDefault();
            if (dom.saveBtn) dom.saveBtn.click();
         }
         return;
      }

      // Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
         e.preventDefault();
         if (timeline) {
            if (timeline.state.selectedAutoClip) {
               timeline.deleteAutoClip(timeline.state.selectedAutoClip);
               timeline.state.selectedAutoClip = null;
            } else {
               timeline.deleteSelected();
            }
         }
      }
   });
}

// ---- Context menu ----
function initContextMenu() {
   if (!dom.contextMenu || !timeline) return;
   timeline.setContextMenuEl(dom.contextMenu);

   // Wire context menu actions
   const items = dom.contextMenu.querySelectorAll('.lw-ctx-item[data-action]');
   items.forEach(item => {
      item.addEventListener('click', () => {
         const action = item.dataset.action;
         switch (action) {
            case 'cut': timeline.cutSelected(); break;
            case 'copy': timeline.copySelected(); break;
            case 'paste': timeline.pasteAt(timeline.state.contextBeat !== undefined ? timeline.state.contextBeat : timeline.state.playheadBeat); break;
            case 'duplicate': timeline.duplicateSelected(); break;
            case 'split': timeline.splitSelected(); break;
            case 'reverse': timeline.reverseSelected(); break;
            case 'delete': timeline.deleteSelected(); break;
            case 'auto-copy': {
               const autoClip = timeline.state.contextAutoClip;
               if (autoClip) {
                  timeline.copyAutoClip(autoClip);
               }
               break;
            }
            case 'auto-paste': {
               const beat = timeline.state.contextBeat !== undefined ? timeline.state.contextBeat : timeline.state.playheadBeat;
               // Paste to the auto clipboard's source track, or the context auto lane's track
               let targetTrackId = null;
               if (timeline.state.autoClipboard) {
                  targetTrackId = timeline.state.autoClipboard.parentId;
               } else if (timeline.state.contextAutoParentId) {
                  targetTrackId = timeline.state.contextAutoParentId;
               } else if (timeline.state.contextAutoClip) {
                  targetTrackId = timeline.state.contextAutoClip.parentId;
               }
               timeline.pasteAutoClip(beat, targetTrackId);
               break;
            }
            case 'auto-new-same': {
               // Create a new segment on the same auto lane (same track + param)
               if (timeline.state.contextAutoParentId && timeline.state.contextAutoParam) {
                  timeline.createNewAutoSegment(timeline.state.contextAutoParentId, timeline.state.contextAutoParam, 0);
               }
               break;
            }
            case 'auto-duplicate': {
               const autoClip = timeline.state.contextAutoClip;
               if (autoClip) {
                  timeline.duplicateAutoClip(autoClip);
               }
               break;
            }
            case 'auto-edit': {
               const autoClip = timeline.state.contextAutoClip;
               if (autoClip) {
                  // Use the segment directly — each segment has its own curve
                  if (!autoClip.trackId) autoClip.trackId = autoClip.parentId;
                  showPanel('auto');
                  if (autoEditor) autoEditor.loadClip(autoClip);
               }
               break;
            }
            case 'auto-delete': {
               const autoClip = timeline.state.contextAutoClip;
               if (autoClip) {
                  timeline.deleteAutoClip(autoClip);
               }
               break;
            }
            case 'auto-reset': {
               const autoClip = timeline.state.contextAutoClip;
               if (autoClip) {
                  // Push history so the reset is undoable
                  if (timeline.pushHistoryForExternal) timeline.pushHistoryForExternal();
                  // Reset to a flat line at the midpoint of the param range
                  const min = autoClip.min !== undefined ? autoClip.min : 0;
                  const max = autoClip.max !== undefined ? autoClip.max : 1;
                  const val = min + (max - min) * 0.5;
                  autoClip.points = [ { x: 0, y: val }, { x: 1, y: val } ];
                  // Invalidate interpolator cache so playback uses the reset curve
                  if (automation.invalidateInterpolator) automation.invalidateInterpolator(autoClip);
                  // Refresh the auto editor if it's showing this clip
                  if (autoEditor && autoEditor.state.currentClip === autoClip) {
                     autoEditor.loadClip(autoClip);
                  }
                  timeline.render();
               }
               break;
            }
         }
         dom.contextMenu.hidden = true;
      });
   });
}

// ---- Timeline + track list wiring ----
// Build track list for mixer, merging track-list props with engine mixer props
function getMixerTracks() {
   if (!trackList) return [];
   const tracks = trackList.getTracks();
   if (!engine) return tracks;
   return tracks.map(t => {
      const engTrack = engine.getTrack(t.id);
      return {
         id: t.id,
         name: t.name,
         color: t.color,
         muted: t.muted,
         solo: t.solo,
         mixerVolume: engTrack ? engTrack.mixerVolume : 0.6,
         mixerPanVal: engTrack ? engTrack.mixerPanVal : 0,
         eqLowGain: engTrack ? engTrack.eqLowGain : 0,
         eqMidGain: engTrack ? engTrack.eqMidGain : 0,
         eqHiGain: engTrack ? engTrack.eqHiGain : 0,
      };
   });
}

function initTimelineAndTracks() {
   // Create timeline canvas
   timeline = createTimeline(dom.canvas, {
      onEventSelected: (selection) => {
         if (selection && selection.trackId && trackList) {
            trackList.selectTrack(selection.trackId);
         }
      },
      onEventCreated: () => {},
      onEventDeleted: (trackId, event) => {
         // Remove from engine
         if (engine) {
            const track = engine.getTrack(trackId);
            if (track) {
               const idx = track.events.findIndex(e =>
                  e.startBeat === event.startBeat && e.lengthBeats === event.lengthBeats);
               if (idx >= 0) track.events.splice(idx, 1);
            }
         }
      },
      onPlayheadMoved: (beat) => {
         if (engine) {
            engine.state.pauseOffset = (beat / engine.state.bpm) * 60;
         }
      },
      onEventsChanged: () => {
         // Bake stretched buffers: if an event was stretched and then resized
         // without Ctrl, replace its buffer with the stretched version so future
         // trim/loop uses the baked buffer as the new natural source.
         const tracks = timeline ? timeline.state.tracks : [];
         for (const track of tracks) {
            for (const event of track.events) {
               if (event._needsBake && event.buffer) {
                  const eng = ensureEngine();
                  if (eng && eng.ctx) {
                     const bpm = timeline.state.bpm || 120;
                     const eventDurSec = (event.lengthBeats / bpm) * 60;
                     const bufDurSec = event.buffer.duration;
                     const ratio = eventDurSec / bufDurSec;
                     if (ratio > 0.97 && ratio < 1.03) {
                        // Near 1:1, no stretching needed
                        event.stretched = false;
                     } else {
                        try {
                           let baked = stretchBufferOffline(eng.ctx, event.buffer, ratio);
                           // WSOLA may produce slightly fewer samples than requested.
                           // Pad with silence to match the exact target duration.
                           const targetFrames = Math.round(eventDurSec * baked.sampleRate);
                           let audioEndFrames = baked.length; // actual audio length before padding
                           if (baked.length < targetFrames) {
                              const padded = eng.ctx.createBuffer(baked.numberOfChannels, targetFrames, baked.sampleRate);
                              for (let ch = 0; ch < baked.numberOfChannels; ch++) {
                                 const src = baked.getChannelData(ch);
                                 const dst = padded.getChannelData(ch);
                                 dst.set(src, 0);
                                 // Remaining samples are already zero (silence)
                              }
                              baked = padded;
                           }
                           // Store the actual audio end for seamless looping
                           // (loopEnd should be the audio length, not including silence padding)
                           event._bakedAudioEndSec = audioEndFrames / baked.sampleRate;
                           event.buffer = baked;
                           event.stretched = false;
                        } catch (e) {
                           console.warn('[dawseur] bake stretch failed:', e);
                           event.stretched = false;
                        }
                     }
                  }
                  delete event._needsBake;
                  // Clear any cached stretched buffer
                  event._stretchedCache = null;
                  event._stretchedCacheKey = null;
               }
            }
         }
         syncEngineEvents();
      },
      onLoopChanged: (enabled, start, end) => {
         const eng = ensureEngine();
         eng.setLoop(enabled, start, end);
      },
      onAutomationClick: (autoTrack) => {
         // Click on an auto clip — switch to auto panel and load in editor
         // Always use the timeline segment directly (not the Map clip),
         // because each segment has its own independent curve points.
         if (!autoTrack) return;
         if (!autoTrack.trackId) autoTrack.trackId = autoTrack.parentId;
         showPanel('auto');
         if (autoEditor) {
            autoEditor.loadClip(autoTrack);
            setTimeout(() => { autoEditor.resize(); }, 50);
         }
      },
      onAutoLaneClick: (parentId, paramName) => {
         // Click on empty area of an auto lane — switch to auto panel
         showPanel('auto');
      },
      onAutomationDragged: (autoTrack) => {
         // Sync automation clip changes back to the automation module
         if (!autoTrack) return;
         const clipKey = autoTrack.parentId + ':' + autoTrack.paramName;
         const clip = automation.automationClips.get(clipKey);
         if (clip) {
            clip.startBeat = autoTrack.startBeat;
            clip.lengthBeats = autoTrack.lengthBeats;
         }
         // Don't call refreshAutomationLanes — it would wipe pasted/duplicated segments.
         // The timeline already has the updated positions from the drag.
         // Just update trackIdx positions for all auto tracks.
         updateAutoTrackIndices();
      },
      onGetAutomationRange: (trackId, paramName) => {
         // Return min/max from the automation Map clip if it exists
         const clipKey = trackId + ':' + paramName;
         const clip = automation.automationClips.get(clipKey);
         if (clip) {
            return { min: clip.min, max: clip.max };
         }
         return null;
      },
   });

   // Create track list
   trackList = createTrackList({
      onTrackAdded: (track) => {
         // Don't create engine during init — defer until first user gesture
         if (engine) {
            engine.addTrack(track.id);
         }
         // Sync timeline tracks (unless suppressed during song load)
         if (!suppressTimelineSync) syncTimelineTracks();
      },
      onTrackRemoved: (trackId) => {
         if (engine) engine.removeTrack(trackId);
         if (!suppressTimelineSync) syncTimelineTracks();
      },
      onTrackSelected: (track) => {
      },
      onVolumeChange: (trackId, vol) => {
         const eng = ensureEngine();
         eng.setTrackVolume(trackId, vol);
      },
      onPanChange: (trackId, pan) => {
         const eng = ensureEngine();
         eng.setTrackPan(trackId, pan);
      },
      onMuteChange: (trackId, muted) => {
         const eng = ensureEngine();
         eng.setTrackMute(trackId, muted);
      },
      onSoloChange: (trackId, solo) => {
         // TODO: implement solo logic in engine
      },
      onFxToggle: (trackId, fxEnabled) => {
         const eng = ensureEngine();
         if (eng.setTrackFx) eng.setTrackFx(trackId, fxEnabled);
      },
      onAutomationClick: (action, autoTrack) => {
         if (action === 'delete') {
            // Delete from Map if exists
            const clipKey = autoTrack.parentId + ':' + autoTrack.paramName;
            automation.automationClips.delete(clipKey);
            // Also remove from timeline autoTracks
            timeline.deleteAutoClip(autoTrack);
         } else if (action === 'edit') {
            // Use the segment directly — each has its own curve
            if (!autoTrack.trackId) autoTrack.trackId = autoTrack.parentId;
            showPanel('auto');
            if (autoEditor) autoEditor.loadClip(autoTrack);
         }
      },
   });

   // Initialize automation system (wires window.createAutomation for faderH context menu)
   automation.initAutomation({
      getTrackId: () => {
         if (trackList) {
            const sel = trackList.getSelectedTrack();
            return sel ? sel.id : null;
         }
         return null;
      },
      onClipCreated: (clip) => {
         refreshAutomationLanes();
         showPanel('auto');
         if (autoEditor) autoEditor.loadClip(clip);
      },
      onClipEdited: (clip) => {
         refreshAutomationLanes();
         showPanel('auto');
         if (autoEditor) autoEditor.loadClip(clip);
      },
   });

   function refreshAutomationLanes() {
      if (!timeline) return;
      const audioTracks = trackList ? trackList.getTracks() : [];
      const existingAutoTracks = timeline.state.autoTracks || [];

      // Register Map clips with trackList (for lane management)
      for (const [key, clip] of automation.automationClips) {
         if (trackList && trackList.addAutomationTrack) {
            trackList.addAutomationTrack(clip.trackId, clip.paramName, clip);
         }
      }
      // Remove automation tracks that no longer exist in the Map AND aren't in existingAutoTracks
      if (trackList && trackList.getAutomationTracks) {
         const tlAutos = trackList.getAutomationTracks();
         for (const a of tlAutos) {
            const clipKey = a.parentId + ':' + a.paramName;
            const inMap = automation.automationClips.has(clipKey);
            const inTimeline = existingAutoTracks.some(e => e.parentId === a.parentId && e.paramName === a.paramName);
            if (!inMap && !inTimeline && trackList.removeAutomationTrack) {
               trackList.removeAutomationTrack(a.id);
            }
         }
      }

      // Build the merged autoTracks list:
      // 1. Start with all existing timeline autoTracks (preserves pasted/duplicated segments)
      // 2. Add any Map clips that don't have a corresponding entry in existingAutoTracks
      const mapKeys = new Set();
      for (const [key] of automation.automationClips) mapKeys.add(key);

      const autoTracksData = existingAutoTracks.map(e => ({ ...e, points: e.points ? e.points.map(p => ({ ...p })) : [] }));

      // Add Map clips that are new (not yet in autoTracksData by id)
      const existingIds = new Set(autoTracksData.map(a => a.id));
      for (const [key, clip] of automation.automationClips) {
         // The Map clip's id is the key; check if it's already in the timeline
         const clipId = key; // Map clips use the key as their identity
         if (!existingIds.has(clipId) && !autoTracksData.some(a => a.parentId === clip.trackId && a.paramName === clip.paramName && a.id === clipId)) {
            autoTracksData.push({
               id: clipId,
               parentId: clip.trackId,
               paramName: clip.paramName,
               points: clip.points ? clip.points.map(p => ({ ...p })) : [],
               startBeat: clip.startBeat || 0,
               lengthBeats: clip.lengthBeats || 4,
               min: clip.min !== undefined ? clip.min : 0,
               max: clip.max !== undefined ? clip.max : 1,
               inputEl: clip.inputEl || null,
            });
         }
         // Also update existing entries that match this Map clip (sync points/length from editor)
         // Only match by id to avoid updating unrelated segments in the same track
         for (const a of autoTracksData) {
            if (a.id === clipId) {
               a.points = clip.points ? clip.points.map(p => ({ ...p })) : a.points;
               a.min = clip.min !== undefined ? clip.min : a.min;
               a.max = clip.max !== undefined ? clip.max : a.max;
               a.lengthBeats = clip.lengthBeats || a.lengthBeats;
            }
         }
      }

      // Assign trackIdx based on interleaved lane positions
      updateAutoTrackIndicesData(autoTracksData, audioTracks);
      timeline.setAutoTracks(autoTracksData);
   }

   function updateAutoTrackIndicesData(autoTracksData, audioTracks) {
      let laneIdx = 0;
      for (const audioTrack of audioTracks) {
         laneIdx++; // audio track takes one lane
         // Group auto clips by paramName — one lane per unique param
         const seenParams = new Set();
         for (const auto of autoTracksData) {
            if (String(auto.parentId) === String(audioTrack.id)) {
               if (!seenParams.has(auto.paramName)) {
                  seenParams.add(auto.paramName);
                  laneIdx++;
               }
               auto.trackIdx = laneIdx - 1;
            }
         }
      }
      // Handle orphans (parent deleted)
      const orphanParams = new Set();
      for (const auto of autoTracksData) {
         const hasParent = audioTracks.some(t => String(t.id) === String(auto.parentId));
         if (!hasParent) {
            if (!orphanParams.has(auto.paramName)) {
               orphanParams.add(auto.paramName);
               laneIdx++;
            }
            auto.trackIdx = laneIdx - 1;
         }
      }
   }

   function updateAutoTrackIndices() {
      if (!timeline) return;
      const audioTracks = trackList ? trackList.getTracks() : [];
      updateAutoTrackIndicesData(timeline.state.autoTracks, audioTracks);
      timeline.render();
   }

   // Add 8 default tracks
   if (trackList) {
      for (let i = 1; i <= 8; i++) {
         trackList.addTrack(`Track ${i}`);
      }
      // Auto-select Track 1 on startup
      trackList.selectTrack(1);
   }

   // ---- Sync vertical scroll between track-list and timeline ----
   const trackListEl = document.getElementById('lw-track-list');
   if (trackListEl && timeline) {
      trackListEl.addEventListener('scroll', () => {
         timeline.setScrollTop(trackListEl.scrollTop);
      });
   }

   // ---- Wire scrollbar ----
   if (dom.scrollbar && dom.scrollbarHandle && timeline) {
      timeline.setScrollbarEls(dom.scrollbar, dom.scrollbarHandle);
   }

   // ---- FX Rack ----
   fxRack = createFxRack({
      engine: null, // will be set when engine is created
      onCreateAutomation: (trackId, fxObject, paramName, inputEl) => {
         console.log('[dawseur] create automation:', trackId, paramName);
         // TODO: implement automation clips in timeline
      },
      onBeforeFxChange: () => {
         // Push history before FX add/remove so it's undoable
         if (timeline) timeline.pushHistoryForExternal();
      },
   });

   // ---- FX chain snapshot/restore for undo/redo ----
   // Serializes the engine's FX chains to plain data (type + params + bypassed)
   // so they can be stored in the timeline's undo/redo history.
   function snapshotFxChains() {
      if (!engine) return null;
      const result = {};
      for (const track of engine.state.tracks) {
         const chain = engine.getTrackFx(track.id);
         if (chain && chain.length > 0) {
            result[track.id] = chain.map(fx => {
               const def = fx._def || {};
               const params = {};
               if (def.inputs && fx._controls) {
                  for (const input of def.inputs) {
                     const el = fx._controls[input.name];
                     if (el) {
                        params[input.name] = el.type === 'select-one' ? el.value : parseFloat(el.value);
                     }
                  }
               }
               return {
                  type: def.name || 'unknown',
                  bypassed: !!fx._bypassed,
                  params: params,
               };
            });
         }
      }
      return result;
   }

   function restoreFxChains(fxData) {
      if (!engine || !fxRack) return;
      if (!fxData) {
         // No FX data in snapshot — clear all FX chains
         for (const track of engine.state.tracks) {
            const chain = engine.getTrackFx(track.id);
            while (chain && chain.length > 0) {
               engine.removeTrackFx(track.id, 0);
            }
         }
         if (fxRack.showTrack && fxRack.state.currentTrackId !== null) {
            fxRack.showTrack(fxRack.state.currentTrackId);
         }
         return;
      }
      // Clear all existing FX chains, then rebuild from snapshot
      for (const track of engine.state.tracks) {
         const chain = engine.getTrackFx(track.id);
         while (chain && chain.length > 0) {
            engine.removeTrackFx(track.id, 0);
         }
      }
      // Rebuild FX chains from snapshot data
      for (const [trackId, chainDef] of Object.entries(fxData)) {
         if (fxRack.setCurrentTrack) fxRack.setCurrentTrack(trackId);
         for (const fxDef of chainDef) {
            if (fxRack.addEffectByName) {
               fxRack.addEffectByName(fxDef.type, fxDef.params || {}, fxDef.bypassed || false);
            }
         }
      }
      // Refresh the FX rack UI for the currently selected track
      if (fxRack.showTrack && fxRack.state.currentTrackId !== null) {
         fxRack.showTrack(fxRack.state.currentTrackId);
      }
   }

   // Wire the snapshot/restore callbacks to the timeline
   if (timeline) {
      timeline.setFxChainCallbacks(snapshotFxChains, restoreFxChains);
   }

   // ---- Mixer ----
   mixer = createMixer({
      engine: null,
      onVolumeChange: (trackId, vol) => {
         const eng = ensureEngine();
         if (eng && eng.setTrackMixerVolume) eng.setTrackMixerVolume(trackId, vol);
      },
      onPanChange: (trackId, pan) => {
         const eng = ensureEngine();
         if (eng && eng.setTrackMixerPan) eng.setTrackMixerPan(trackId, pan);
      },
      onMuteChange: (trackId, muted) => {
         const eng = ensureEngine();
         if (eng) eng.setTrackMute(trackId, muted);
      },
      onSoloChange: (trackId, solo) => {
         // TODO: implement solo logic
      },
      onEqChange: (trackId, band, gain) => {
         const eng = ensureEngine();
         if (eng && eng.setTrackEq) eng.setTrackEq(trackId, band, gain);
      },
      onMasterVolume: (vol) => {
         const eng = ensureEngine();
         if (eng && eng.setMasterVolume) eng.setMasterVolume(vol);
      },
      onMasterPan: (pan) => {
         const eng = ensureEngine();
         if (eng && eng.setMasterPan) eng.setMasterPan(pan);
      },
      onMasterEq: (band, gain) => {
         const eng = ensureEngine();
         if (eng && eng.setMasterEq) eng.setMasterEq(band, gain);
      },
      onMasterComp: (enabled) => {
         const eng = ensureEngine();
         if (eng && eng.setMasterComp) eng.setMasterComp(enabled);
      },
      onMasterCompParam: (param, value) => {
         const eng = ensureEngine();
         if (eng && eng.setMasterCompParam) eng.setMasterCompParam(param, value);
      },
   });

   // Initialize mixer with current tracks (merged with engine mixer values)
   if (mixer && trackList) {
      // Engine may have been created already (during addTrack) before mixer existed
      if (engine && mixer.setEngine) mixer.setEngine(engine);
      mixer.setTracks(getMixerTracks());
   }

   // ---- Panel tab toggle (FX / Mixer) ----
   const tabFx = document.getElementById('lw-tab-fx');
   const tabMixer = document.getElementById('lw-tab-mixer');
   const tabAuto = document.getElementById('lw-tab-auto');
   const tabStepSeq = document.getElementById('lw-tab-stepseq');
   const tabSlicer = document.getElementById('lw-tab-slicer');
   const tabPianoRoll = document.getElementById('lw-tab-pianoroll');
   const autoBody = document.getElementById('lw-auto-body');
   const fxRackBody = document.getElementById('lw-fx-rack-body');
   const mixerBody = document.getElementById('lw-mixer-body');
   const stepSeqBody = document.getElementById('lw-stepseq-body');
   const slicerBody = document.getElementById('lw-slicer-body');
   const pianoRollBody = document.getElementById('lw-pianoroll-body');
   const fxAddBtn = document.getElementById('lw-fx-add');
   const fxRackTitle = document.getElementById('lw-fx-rack-title');

   const allTabs = [tabFx, tabMixer, tabAuto, tabStepSeq, tabSlicer, tabPianoRoll];
   const allBodies = [fxRackBody, mixerBody, autoBody, stepSeqBody, slicerBody, pianoRollBody];

   const panelTitles = {
      mixer: 'Mixer',
      auto: 'Automation Editor',
      stepseq: 'Step Sequencer',
      slicer: 'Slicer',
      pianoroll: 'Piano Roll',
   };

   showPanel = function(panel) {
      const panelMap = {
         fx: { tab: tabFx, body: fxRackBody },
         mixer: { tab: tabMixer, body: mixerBody },
         auto: { tab: tabAuto, body: autoBody },
         stepseq: { tab: tabStepSeq, body: stepSeqBody },
         slicer: { tab: tabSlicer, body: slicerBody },
         pianoroll: { tab: tabPianoRoll, body: pianoRollBody },
      };
      const entry = panelMap[panel] || panelMap.fx;
      // Deactivate all tabs, hide all bodies
      for (const t of allTabs) { if (t) t.classList.remove('active'); }
      for (const b of allBodies) { if (b) b.hidden = true; }
      // Activate selected
      if (entry.tab) entry.tab.classList.add('active');
      if (entry.body) entry.body.hidden = false;
      // FX add button only visible on FX panel
      if (fxAddBtn) fxAddBtn.style.display = (panel === 'fx') ? '' : 'none';
      // Title
      if (fxRackTitle) {
         if (panel === 'fx') {
            const sel = trackList ? trackList.getSelectedTrack() : null;
            fxRackTitle.textContent = sel ? `FX Rack — ${sel.id}` : 'FX Rack — select a track';
         } else {
            fxRackTitle.textContent = panelTitles[panel] || '';
         }
      }
      // Panel-specific actions
      if (panel === 'mixer' && mixer) mixer.render();
      if (panel === 'auto' && autoEditor) autoEditor.resize();
      // Stop step sequencer preview when leaving the step seq panel
      if (panel !== 'stepseq' && stepSequencer && stepSequencer.stopPreview) {
         stepSequencer.stopPreview();
      }
   }

   domInitialized = true;
   if (tabFx) tabFx.addEventListener('click', () => showPanel('fx'));
   if (tabMixer) tabMixer.addEventListener('click', () => showPanel('mixer'));
   if (tabAuto) tabAuto.addEventListener('click', () => showPanel('auto'));
   if (tabStepSeq) tabStepSeq.addEventListener('click', () => showPanel('stepseq'));
   if (tabSlicer) tabSlicer.addEventListener('click', () => showPanel('slicer'));
   if (tabPianoRoll) tabPianoRoll.addEventListener('click', () => showPanel('pianoroll'));

   // ---- Create automation editor ----
   autoEditor = createAutoEditor({
      onBeforeEdit: function() {
         // Push history before curve edits so undo/redo works
         if (timeline) timeline.pushHistoryForExternal();
      },
      onClipChanged: function(clip) {
         // The editor modified the clip object in place.
         // Sync back to the Map clip if needed (for playback engine).
         const clipKey = clip.trackId + ':' + clip.paramName;
         const mapClip = automation.automationClips.get(clipKey);
         if (mapClip && mapClip !== clip) {
            mapClip.points = clip.points.map(p => ({ ...p }));
            mapClip.lengthBeats = clip.lengthBeats;
            if (clip.isKnob) {
               mapClip.isKnob = true;
               mapClip.setValue = clip.setValue;
               mapClip.getValue = clip.getValue;
               mapClip.element = clip.element;
            }
         }
         // Invalidate interpolator cache for this clip so playback uses new points
         if (automation.invalidateInterpolator) automation.invalidateInterpolator(clip);

         // Sync the edited points directly into the timeline's autoTracks data
         // so the sequencer shows real-time changes (timeline.render() alone
         // would re-render with stale copied points).
         // Match by clip id to only update the specific segment being edited,
         // not all segments in the same automation track.
         if (timeline && timeline.state.autoTracks) {
            const clipId = clip.id || clipKey;
            for (const a of timeline.state.autoTracks) {
               if (a.id === clipId) {
                  a.points = clip.points.map(p => ({ ...p }));
                  a.lengthBeats = clip.lengthBeats || a.lengthBeats;
                  a.min = clip.min !== undefined ? clip.min : a.min;
                  a.max = clip.max !== undefined ? clip.max : a.max;
                  break; // Only update the matching segment
               }
            }
         }

         // Apply current automation value immediately (real-time feedback while editing)
         if (automation.setExternalAutoTracks && timeline && timeline.state.autoTracks) {
            automation.setExternalAutoTracks(timeline.state.autoTracks);
         }
         if (engine && engine.state.isPlaying) {
            const beat = engine.getPositionBeats();
            automation.tickAutomations(beat, engine);
         } else if (engine) {
            const beat = timeline ? timeline.state.playheadBeat : 0;
            automation.tickAutomations(beat, engine);
         }
         if (timeline) timeline.render();
      },
   });

   // ---- Create step sequencer ----
   stepSequencer = createStepSequencer({
      getBucketItems: () => {
         if (bucket && bucket.items) return bucket.items;
         return [];
      },
      getEngine: () => ensureEngine(),
      onChange: () => { syncMIDIClipsSoft(); if (timeline) timeline.render(); },
      resolveDropItem: resolveDropItem,
   });

   // ---- Create slicer ----
   slicer = createSlicer({
      getBucketItems: () => {
         if (bucket && bucket.items) return bucket.items;
         return [];
      },
      getEngine: () => ensureEngine(),
      resolveDropItem: resolveDropItem,
      loadBuffer: loadItemBuffer,
      onChange: () => { syncMIDIClipsSoft(); if (timeline) timeline.render(); },
   });

   // ---- Create piano roll ----
   pianoRoll = createPianoRoll({
      getBucketItems: () => {
         if (bucket && bucket.items) return bucket.items;
         return [];
      },
      getEngine: () => ensureEngine(),
      resolveDropItem: resolveDropItem,
      loadBuffer: loadItemBuffer,
      onChange: () => { syncMIDIClipsSoft(); if (timeline) timeline.render(); },
   });

   // ---- Wire offline sample loader for MIDI clips ----
   if (setOfflineSampleLoader) {
      setOfflineSampleLoader(async (sampleId) => {
         if (!bucket || !bucket.items) return null;
         const item = bucket.items.find(it => it.id === sampleId);
         if (!item) return null;
         let arrayBuffer;
         if (item.wav instanceof ArrayBuffer) arrayBuffer = item.wav;
         else if (item.wav instanceof Uint8Array) arrayBuffer = item.wav.buffer;
         else if (item.wavLike instanceof ArrayBuffer) arrayBuffer = item.wavLike;
         else if (item.wavLike instanceof Uint8Array) arrayBuffer = item.wavLike.buffer;
         else if (item.data instanceof ArrayBuffer) arrayBuffer = item.data;
         else if (item.data instanceof Uint8Array) arrayBuffer = item.data.buffer;
         else return null;
         // Use a temporary AudioContext for decoding (offline context can't decode)
         const tempCtx = new (window.AudioContext || window.webkitAudioContext)();
         try {
            const buf = await tempCtx.decodeAudioData(arrayBuffer.slice(0));
            tempCtx.close();
            return buf;
         } catch (e) {
            tempCtx.close();
            return null;
         }
      });
   }

   // Wire track selection to show FX rack and update mixer
   if (trackList) {
      const origOnTrackSelected = trackList.state.onTrackSelected;
      trackList.state.onTrackSelected = (track) => {
         if (fxRack) {
            const eng = ensureEngine();
            fxRack.state.engine = eng;
            fxRack.showTrack(track ? track.id : null);
         }
         // Don't rebuild mixer on track selection — it would reset fader positions.
         // The mixer shows all tracks and doesn't need to change on selection.
         // Update title if FX panel visible
         if (fxRackTitle && tabFx && tabFx.classList.contains('active')) {
            fxRackTitle.textContent = track ? `FX Rack — ${track.id}` : 'FX Rack — select a track';
         }
      };
      // Auto-select Track 1 on startup (after wiring is complete)
      trackList.selectTrack(1);
   }
}

function syncTimelineTracks() {
   if (!timeline || !trackList) return;
   const tracks = trackList.getTracks().map(t => ({
      id: t.id,
      name: t.name,
      color: t.color,
      events: [],
   }));
   // Preserve existing events
   for (const tlTrack of timeline.state.tracks) {
      const newTrack = tracks.find(t => t.id === tlTrack.id);
      if (newTrack) newTrack.events = tlTrack.events;
   }
   timeline.setTracks(tracks);
}

// ---- Sync engine events from timeline ----
// Copies event positions/volumes/fades from timeline to engine,
// then re-schedules if currently playing.
function syncEngineEvents() {
   if (!timeline || !engine) return;
   for (const tlTrack of timeline.state.tracks) {
      const engTrack = engine.getTrack(tlTrack.id);
      if (!engTrack) continue;
      // Replace engine events with timeline events (including per-event buffer)
      engTrack.events = tlTrack.events.map(ev => {
         const engEv = {
            startBeat: ev.startBeat,
            lengthBeats: ev.lengthBeats,
            volume: ev.volume,
            stretched: ev.stretched || false,
            buffer: ev.buffer,
            acidMeta: ev.acidMeta,
            fadeInBeats: ev.fadeInBeats || 0,
            fadeOutBeats: ev.fadeOutBeats || 0,
            fadeInCp1Y: ev.fadeInCp1Y, fadeInCp2Y: ev.fadeInCp2Y,
            fadeOutCp1Y: ev.fadeOutCp1Y, fadeOutCp2Y: ev.fadeOutCp2Y,
         };
         // MIDI clip types: copy type + embedded data
         if (ev.type === 'stepseq' || ev.type === 'slicer' || ev.type === 'pianoroll') {
            engEv.type = ev.type;
            engEv.sequence = ev.sequence;
            engEv.pattern = ev.pattern;
            // NOTE: do NOT override lengthBeats here. The clip length is
            // independent of the sequence/pattern natural length — the
            // sequence loops to fill whatever the user resized the clip to.
            // Overwriting would undo timeline resize drags.
         }
         return engEv;
      });
   }
   // If playing, re-schedule with updated positions
   if (engine.state.isPlaying) {
      engine.reschedule();
   }
}

// ---- Soft sync for MIDI clip edits (step seq / slicer / piano roll) ----
// Unlike syncEngineEvents, this does NOT call reschedule() (which would
// stopAllSources and clear scheduledMIDISteps, cutting off currently-playing
// samples and skipping past steps in the current loop iteration).
// The scheduler already re-evaluates MIDI clips every 25ms tick using the
// live sequence/pattern references, so edits propagate automatically.
// We do NOT touch lengthBeats here — the clip length is the user's choice
// (set at drop time or via timeline resize); the sequence loops to fill it.
function syncMIDIClipsSoft() {
   if (!timeline || !engine) return;
   // Ensure engine events reference the live sequence/pattern objects.
   // (They already do from the initial syncEngineEvents on drop, but this
   //  is a no-op safety net in case events were replaced.)
   for (const tlTrack of timeline.state.tracks) {
      const engTrack = engine.getTrack(tlTrack.id);
      if (!engTrack) continue;
      for (let i = 0; i < engTrack.events.length && i < tlTrack.events.length; i++) {
         const engEv = engTrack.events[i];
         const tlEv = tlTrack.events[i];
         if (engEv.type !== tlEv.type) continue;
         if (engEv.type === 'stepseq' || engEv.type === 'slicer') {
            engEv.sequence = tlEv.sequence;
         } else if (engEv.type === 'pianoroll') {
            engEv.pattern = tlEv.pattern;
         }
      }
   }
   // No reschedule() — the scheduler picks up MIDI clip changes every tick.
}

// ---- Drag and drop from bucket to timeline ----
function initDragDrop() {
   if (!dom.timelineArea) return;

   // Show drop hint
   const hint = document.createElement('div');
   hint.className = 'lw-drop-hint';
   hint.textContent = '← drag samples from bucket';
   dom.timelineArea.appendChild(hint);

   dom.timelineArea.addEventListener('dragover', (e) => {
      e.preventDefault();
      dom.timelineArea.classList.add('drag-over');
      hint.style.display = 'none';
   });

   dom.timelineArea.addEventListener('dragleave', (e) => {
      if (e.target === dom.timelineArea) {
         dom.timelineArea.classList.remove('drag-over');
         if (timeline && timeline.state.tracks.length === 0) {
            hint.style.display = 'block';
         }
      }
   });

   dom.timelineArea.addEventListener('drop', async (e) => {
      e.preventDefault();
      dom.timelineArea.classList.remove('drag-over');

      // Compute canvas-relative drop coordinates
      const canvasRect = dom.canvas.getBoundingClientRect();
      const dropX = e.clientX - canvasRect.left;
      const dropY = e.clientY - canvasRect.top;

      // Try to get bucket item or samplebank item
      const item = await resolveDropItem(e.dataTransfer);
      if (item) {
         handleBucketItemDrop(item, dropX, dropY);
         hint.style.display = 'none';
         return;
      }

      // Try step sequencer sequence drop
      const stepSeqData = e.dataTransfer.getData('text/dawseur-stepseq');
      if (stepSeqData) {
         try {
            const parsed = JSON.parse(stepSeqData);
            handleMIDIClipDrop(parsed, dropX, dropY);
            hint.style.display = 'none';
            return;
         } catch (e2) {}
      }

      // Try slicer sequence drop
      const slicerData = e.dataTransfer.getData('text/dawseur-slicer');
      if (slicerData) {
         try {
            const parsed = JSON.parse(slicerData);
            handleMIDIClipDrop(parsed, dropX, dropY);
            hint.style.display = 'none';
            return;
         } catch (e2) {}
      }

      // Try piano roll pattern drop
      const pianoRollData = e.dataTransfer.getData('text/dawseur-pianoroll');
      if (pianoRollData) {
         try {
            const parsed = JSON.parse(pianoRollData);
            handleMIDIClipDrop(parsed, dropX, dropY);
            hint.style.display = 'none';
            return;
         } catch (e2) {}
      }

      // Try file drop (OS files)
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
         for (const file of files) {
            if (file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|flac|ogg|aif+)$/i)) {
               const reader = new FileReader();
               reader.onload = async (ev) => {
                  const arrayBuffer = ev.target.result;
                  handleBucketItemDrop({
                     name: file.name,
                     wav: arrayBuffer,
                  }, dropX, dropY);
                  hint.style.display = 'none';
               };
               reader.readAsArrayBuffer(file);
            }
         }
      }
   });
}

// ---- Handle MIDI clip drop from step sequencer / slicer / piano roll ----
function handleMIDIClipDrop(dragData, dropX, dropY) {
   if (!timeline || !trackList) return;
   const drop = timeline.getDropPosition(dropX, dropY);
   if (!drop.trackId) return;

   let clip = null;

   if (dragData.type === 'stepseq' && stepSequencer) {
      // Use live reference so edits in the step sequencer update in real-time
      const seq = stepSequencer.getSequenceRef(dragData.sequenceIdx);
      if (!seq) return;
      // Calculate clip length: steps / stepsPerBeat at sequence BPM
      const stepsPerBeat = 4;
      const lengthBeats = seq.steps / stepsPerBeat;
      clip = {
         type: 'stepseq',
         startBeat: drop.beat,
         lengthBeats: lengthBeats,
         volume: 0.8,
         stretched: false,
         sequence: seq, // live reference — edits update in real-time
      };
   } else if (dragData.type === 'slicer' && slicer) {
      const seq = slicer.getSequenceRef(dragData.sequenceIdx);
      if (!seq) return;
      const stepsPerBeat = 4;
      const lengthBeats = seq.steps / stepsPerBeat;
      clip = {
         type: 'slicer',
         startBeat: drop.beat,
         lengthBeats: lengthBeats,
         volume: 0.8,
         stretched: false,
         sequence: seq, // live reference
      };
   } else if (dragData.type === 'pianoroll' && pianoRoll) {
      const pat = pianoRoll.getPatternRef(dragData.patternIdx);
      if (!pat) return;
      clip = {
         type: 'pianoroll',
         startBeat: drop.beat,
         lengthBeats: pat.beats,
         volume: 0.8,
         stretched: false,
         pattern: pat, // live reference
      };
   }

   if (clip) {
      timeline.addEventToTrack(drop.trackId, clip);
      // Sync to engine so it knows about the MIDI clip
      syncEngineEvents();
   }
}

// ---- Mobile warning ----
function initMobileWarning() {
   const modal = document.getElementById('mobile-warning-modal');
   const closeBtn = document.getElementById('close-mobile-warning');
   if (!modal || !closeBtn) return;
   if (window.innerWidth < 768) modal.classList.remove('hidden');
   closeBtn.addEventListener('click', () => modal.classList.add('hidden'));
}

   // ---- Export to WAV ----
   if (dom.exportBtn) {
      dom.exportBtn.addEventListener('click', async () => {
         const eng = ensureEngine();
         if (!eng) return;

         const exportModal = document.getElementById('lw-export-modal');
         const progressBar = document.getElementById('lw-export-progress-fill');
         const statusEl = document.getElementById('lw-export-status');
         const cancelBtn = document.getElementById('lw-export-cancel');
         const downloadBtn = document.getElementById('lw-export-download');
         const titleEl = document.getElementById('lw-export-title');

         if (!exportModal) return;

         // Show modal
         exportModal.hidden = false;
         if (downloadBtn) downloadBtn.hidden = true;
         if (cancelBtn) cancelBtn.hidden = false;
         titleEl.textContent = 'Rendering...';
         statusEl.textContent = 'Preparing offline render...';
         progressBar.style.width = '0%';

         // Determine if we should render loop only
         const loopOnly = eng.state.loopEnabled && eng.state.loopEnd > eng.state.loopStart;
         const renderLabel = loopOnly ? 'loop section' : 'full song';
         statusEl.textContent = 'Rendering ' + renderLabel + '...';

         try {
            // Sync timeline autoTracks to automation module for offline render
            if (automation.setExternalAutoTracks && timeline && timeline.state.autoTracks) {
               automation.setExternalAutoTracks(timeline.state.autoTracks);
            }
            const blob = await renderToWav(eng, automation, {
               timeline: timeline,
               loopOnly: loopOnly,
               onProgress: function(pct) {
                  progressBar.style.width = Math.round(pct * 100) + '%';
                  if (pct < 0.1) statusEl.textContent = 'Building audio graph...';
                  else if (pct < 0.2) statusEl.textContent = 'Scheduling events...';
                  else if (pct < 0.3) statusEl.textContent = 'Applying automations...';
                  else if (pct < 0.8) statusEl.textContent = 'Rendering audio...';
                  else if (pct < 1.0) statusEl.textContent = 'Encoding WAV...';
                  else statusEl.textContent = 'Done!';
               }
            });

            // Add rendered WAV to bucket
            progressBar.style.width = '100%';
            titleEl.textContent = 'Render Complete';
            statusEl.textContent = 'Adding to bucket...';
            if (cancelBtn) cancelBtn.hidden = true;
            if (downloadBtn) downloadBtn.hidden = true;
            try {
               const arrayBuffer = await blob.arrayBuffer();
               const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
               const renderName = 'dawseur-' + ts + '.wav';
               if (bucket && bucket.add) {
                  await bucket.add({
                     source: 'dawseur',
                     name: renderName,
                     wavLike: arrayBuffer,
                     fileType: 'wav',
                  });
                  statusEl.textContent = 'Added to bucket: ' + renderName;
               } else {
                  statusEl.textContent = 'Bucket not available';
               }
            } catch (e2) {
               console.error('[export] add to bucket failed:', e2);
               statusEl.textContent = 'Error adding to bucket: ' + (e2.message || 'unknown');
            }
            setTimeout(function() { exportModal.hidden = true; }, 1500);
         } catch (e) {
            console.error('[export] render failed:', e);
            statusEl.textContent = 'Error: ' + (e.message || 'render failed');
            if (cancelBtn) cancelBtn.hidden = false;
         }
      });
   }

   // ---- Save song (.dws) ----
   if (dom.saveBtn) {
      dom.saveBtn.addEventListener('click', async () => {
         const eng = ensureEngine();
         if (!eng) return;
         let songName = (dom.filenameInput && dom.filenameInput.value.trim()) || 'song';
         try {
            const data = await saveSong({
               engine: eng,
               timeline: timeline,
               trackList: trackList,
               automation: automation,
               stepSequencer: stepSequencer,
               slicer: slicer,
               pianoRoll: pianoRoll,
               fxRack: fxRack,
               getMixerTracks: getMixerTracks,
               bucket: bucket,
            });
            const filename = songName.endsWith('.dws') ? songName : songName + '.dws';
            downloadSong(data, filename);
         } catch (e) {
            console.error('[save] failed:', e);
            alert('Save failed: ' + (e.message || 'unknown error'));
         }
      });
   }

   // ---- Load song (.dws) ----
   if (dom.loadBtn && dom.loadFileInput) {
      dom.loadBtn.addEventListener('click', () => {
         dom.loadFileInput.click();
      });
      dom.loadFileInput.addEventListener('change', async (e) => {
         const file = e.target.files[0];
         if (!file) return;
         e.target.value = ''; // reset for next load
         const eng = ensureEngine();
         if (!eng) return;
         suppressTimelineSync = true; // prevent syncTimelineTracks during track rebuild
         try {
            const arrayBuffer = await file.arrayBuffer();
            await loadSong(arrayBuffer, {
               engine: eng,
               timeline: timeline,
               trackList: trackList,
               automation: automation,
               stepSequencer: stepSequencer,
               slicer: slicer,
               pianoRoll: pianoRoll,
               fxRack: fxRack,
               mixer: mixer,
               bucket: bucket,
               ctx: eng.ctx,
               getMixerTracks: getMixerTracks,
               syncEngineEvents: syncEngineEvents,
            });
            console.log('[load] song loaded successfully');
            // Update filename field from loaded file (strip .dws extension)
            if (dom.filenameInput) {
               const name = file.name.replace(/\.dws$/i, '');
               dom.filenameInput.value = name;
            }
         } catch (e) {
            console.error('[load] failed:', e);
            alert('Load failed: ' + (e.message || 'unknown error'));
         } finally {
            suppressTimelineSync = false;
         }
      });
   }

function boot() {
   initMobileWarning();
   initSplit();
   initTransport();
   initTimelineAndTracks();
   initToolbar();
   initContextMenu();
   initDragDrop();
   initBucket();
   initSampleSearch();
   // Prevent default system context menu globally (app handles right-clicks itself)
   document.addEventListener('contextmenu', (e) => e.preventDefault());
}

// ============================================================================
// SAMPLE SEARCH (left panel — browse & search server-side samplebank)
// ============================================================================
let ssAbortController = null;
let ssCurrentFolder = null;
let ssPreviewAudio = null;
let ssPreviewSource = null;

function initSampleSearch() {
   const results = document.getElementById('lw-ss-results');
   const searchInput = document.getElementById('lw-ss-input');
   if (!results) return;

   if (searchInput) {
      let t;
      searchInput.addEventListener('input', () => {
         clearTimeout(t);
         t = setTimeout(() => ssFetch(searchInput.value.trim()), 300);
      });
   }
   ssFetch('');
}

function ssFormatSize(bytes) {
   if (!bytes && bytes !== 0) return '';
   if (bytes < 1024) return bytes + ' B';
   if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'K';
   return (bytes / (1024 * 1024)).toFixed(1) + 'M';
}

async function ssFetch(q) {
   const results = document.getElementById('lw-ss-results');
   if (!results) return;
   if (ssAbortController) { try { ssAbortController.abort(); } catch (_) {} }
   ssAbortController = new AbortController();
   results.innerHTML = '<div class="lw-ss-status">loading…</div>';

   let data = [];
   let isFolderList = false;
   try {
      let url;
      if (q) {
         url = '/media/list?format=samplebank&search=' + encodeURIComponent(q);
      } else if (ssCurrentFolder) {
         url = '/media/list?format=samplebank&folder=' + encodeURIComponent(ssCurrentFolder);
      } else {
         url = '/media/list?format=samplebank';
         isFolderList = true;
      }
      const resp = await fetch(url, { signal: ssAbortController.signal });
      if (resp.ok) data = await resp.json();
   } catch (err) {
      if (err.name === 'AbortError') return;
   }
   if (!Array.isArray(data)) data = [];
   results.innerHTML = '';
   const frag = document.createDocumentFragment();

   if (ssCurrentFolder && !q) {
      const back = document.createElement('div');
      back.className = 'lw-ss-back';
      back.textContent = '\u2190 back to folders';
      back.addEventListener('click', () => {
         ssCurrentFolder = null;
         const si = document.getElementById('lw-ss-input');
         if (si) si.value = '';
         ssFetch('');
      });
      frag.appendChild(back);
      const hdr = document.createElement('div');
      hdr.className = 'lw-ss-folder-header';
      hdr.textContent = ssCurrentFolder + ' (' + data.length + ')';
      frag.appendChild(hdr);
   }

   if (isFolderList) {
      if (!data.length) {
         results.innerHTML = '<div class="lw-ss-status">no sample folders</div>';
         return;
      }
      data.sort((a, b) => (a.folder || '').localeCompare(b.folder || ''));
      for (const f of data) {
         const name = f.folder || f.name || '';
         const div = document.createElement('div');
         div.className = 'lw-ss-folder-item';
         div.textContent = name;
         div.addEventListener('click', () => { ssCurrentFolder = name; ssFetch(''); });
         frag.appendChild(div);
      }
   } else {
      if (!data.length) {
         results.innerHTML = '<div class="lw-ss-status">' + (q ? 'no samples matching "' + q + '"' : 'no samples') + '</div>';
         return;
      }
      data.sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
      for (const item of data) {
         const filename = item.filename || item.name || 'unknown';
         const folder = item.folder || '';
         const div = document.createElement('div');
         div.className = 'lw-ss-item';
         const folderTag = (q && folder) ? '<span class="lw-ss-folder">' + folder + '</span>' : '';
         div.innerHTML = '<span class="lw-ss-name" title="' + filename + '">' + filename + '</span>' + folderTag + '<span class="lw-ss-size">' + ssFormatSize(item.size) + '</span>';
         div.draggable = true;
         div.addEventListener('dragstart', (e) => {
            e.dataTransfer.setData('text/plain', JSON.stringify({
               type: 'samplebank',
               uuid: item.uuid, hash: item.hash, filename: item.filename, name: item.filename,
            }));
            e.dataTransfer.effectAllowed = 'copy';
         });
         div.addEventListener('click', () => {
            document.querySelectorAll('.lw-ss-item').forEach(el => el.classList.remove('active'));
            div.classList.add('active');
            ssPreview(item);
         });
         frag.appendChild(div);
      }
   }
   results.appendChild(frag);
}

function ssPreview(item) {
   if (ssPreviewSource) { try { ssPreviewSource.stop(); } catch (_) {} try { ssPreviewSource.disconnect(); } catch (_) {} }
   const id = item.uuid || item.hash || item.filename;
   if (!id) return;
   const url = '/media/get/' + encodeURIComponent(id) + '?format=samplebank';
   fetch(url).then(r => r.arrayBuffer()).then(ab => {
      if (!ssPreviewAudio) ssPreviewAudio = new (window.AudioContext || window.webkitAudioContext)();
      if (ssPreviewAudio.state === 'suspended') ssPreviewAudio.resume();
      decodeAudioWithFallback(ssPreviewAudio, ab).then(buf => {
         if (!buf) {
            console.warn('[dawseur] preview decode failed for', item.filename);
            return;
         }
         ssPreviewSource = ssPreviewAudio.createBufferSource();
         ssPreviewSource.buffer = buf;
         ssPreviewSource.connect(ssPreviewAudio.destination);
         ssPreviewSource.start(0);
      });
   }).catch(e => {
      console.warn('[dawseur] preview fetch failed for', item.filename, e);
   });
}

boot();
